import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  usePoll, AlpacaStatus, AlpacaHistory, WinRate, Health, fmtUsd,
} from '../lib';

/** Count-up on first paint; instant on subsequent refreshes. */
function useCountUp(target: number | null, ms = 900) {
  const [v, setV] = useState<number | null>(null);
  const done = useRef(false);
  useEffect(() => {
    if (target == null) return;
    if (done.current) { setV(target); return; }
    done.current = true;
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / ms);
      const e = 1 - Math.pow(1 - k, 3);
      setV(target * e);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

function EquityCurve({ pts }: { pts: { t: number; v: number }[] }) {
  if (pts.length < 2) return <div className="curve-empty">collecting equity history…</div>;
  const w = 1000, h = 130, pad = 6;
  const vs = pts.map(p => p.v);
  const min = Math.min(...vs), max = Math.max(...vs);
  const span = max - min || 1;
  const x = (i: number) => (i / (pts.length - 1)) * (w - 2 * pad) + pad;
  const y = (v: number) => h - pad - ((v - min) / span) * (h - 2 * pad);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const base = pts[0].v;
  const lastUp = pts[pts.length - 1].v >= base;
  return (
    <div className="curve-wrap">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="curve">
        <defs>
          <linearGradient id="fillg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lastUp ? 'var(--gain)' : 'var(--drop)'} stopOpacity="0.14" />
            <stop offset="100%" stopColor={lastUp ? 'var(--gain)' : 'var(--drop)'} stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1={pad} x2={w - pad} y1={y(base)} y2={y(base)} className="curve-base" />
        <path d={`${d} L${w - pad},${h} L${pad},${h} Z`} fill="url(#fillg)" stroke="none" />
        <path d={d} className={lastUp ? 'curve-line up' : 'curve-line down'} pathLength={1} />
        <circle cx={x(pts.length - 1)} cy={y(pts[pts.length - 1].v)} r="3.5"
          className={lastUp ? 'curve-dot up' : 'curve-dot down'} />
      </svg>
      <div className="curve-scale">
        <span>{fmtUsd(min)}</span>
        <span>{fmtUsd(max)}</span>
      </div>
    </div>
  );
}

const Pnl = ({ v, pct }: { v: number; pct?: string }) => (
  <span className={v > 0 ? 'pnl up' : v < 0 ? 'pnl down' : 'pnl'}>
    {fmtUsd(v, true)}{pct ? <span className="pnl-pct"> {pct}</span> : null}
  </span>
);

export default function Overview() {
  const { data: st, err } = usePoll<AlpacaStatus>('paper-trading/alpaca-status', 30000);
  const { data: hist } = usePoll<AlpacaHistory>('paper-trading/alpaca-history', 120000);
  const { data: wr } = usePoll<WinRate>('paper-trading/alpaca-win-rate', 60000);
  const { data: health } = usePoll<Health>('health', 60000);

  const equity = st ? parseFloat(st.account.equity) : null;
  const shown = useCountUp(equity);
  const dayPnl = st ? parseFloat(st.account.equity) - parseFloat(st.account.last_equity) : null;
  const totPnl = st ? parseFloat(st.account.equity) - parseFloat(st.account.initial_equity || '100000') : null;

  const pts = useMemo(() => {
    const h = (hist?.equity_history ?? []).map(p => ({ t: p.timestamp, v: p.equity }));
    if (equity != null) h.push({ t: Date.now() / 1000, v: equity });
    return h;
  }, [hist, equity]);

  if (err) return <div className="fault">API UNREACHABLE — the backend is not answering. The bot may still be running; check the worker.</div>;

  return (
    <main className="overview">
      <section className="hero rise" style={{ animationDelay: '0ms' }}>
        <div className="hero-figure">
          <span className="hero-label">account equity</span>
          <span className="hero-equity">
            {shown == null ? '—' : `$${shown.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </span>
          <div className="hero-deltas">
            {dayPnl != null && <span className="hero-delta"><em>today</em> <Pnl v={dayPnl} /></span>}
            {totPnl != null && <span className="hero-delta"><em>all-time</em> <Pnl v={totPnl} /></span>}
            {wr && (
              <span className="hero-delta">
                <em>closed</em>&nbsp;{wr.all_time.wins}W&thinsp;/&thinsp;{wr.all_time.losses}L
              </span>
            )}
          </div>
        </div>
        <EquityCurve pts={pts} />
      </section>

      <section className="ledger rise" style={{ animationDelay: '90ms' }}>
        <div className="rule"><span className="rule-no">01</span><span className="rule-name">Open positions</span>
          <span className="rule-meta">{st?.positions.length ?? '—'} short</span></div>
        {st && st.positions.length === 0 && <div className="empty">flat — no open positions</div>}
        {st && st.positions.length > 0 && (
          <table className="tbl">
            <thead><tr><th>sym</th><th>qty</th><th>entry</th><th>last</th><th className="r">unrealized</th></tr></thead>
            <tbody>
              {st.positions.map(p => {
                const u = parseFloat(p.unrealized_pl);
                return (
                  <tr key={p.symbol}>
                    <td className="sym">{p.symbol}<span className="side">▼</span></td>
                    <td>{Math.abs(parseInt(p.qty, 10))}</td>
                    <td>{fmtUsd(parseFloat(p.avg_entry_price))}</td>
                    <td>{fmtUsd(parseFloat(p.current_price))}</td>
                    <td className="r"><Pnl v={u} pct={`${(parseFloat(p.unrealized_plpc) * 100).toFixed(1)}%`} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="ledger rise" style={{ animationDelay: '180ms' }}>
        <div className="rule"><span className="rule-no">02</span><span className="rule-name">Recent closed</span>
          <span className="rule-meta">{wr ? fmtUsd(wr.all_time.realized_pnl, true) : '—'} realized</span></div>
        {wr && (
          <table className="tbl">
            <thead><tr><th>sym</th><th>qty</th><th>entry</th><th>exit</th><th>closed</th><th className="r">p&amp;l</th></tr></thead>
            <tbody>
              {wr.recent_closed.slice(0, 8).map((t, i) => (
                <tr key={`${t.symbol}-${t.exit_time}-${i}`}>
                  <td className="sym">{t.symbol}<span className="side">▼</span></td>
                  <td>{t.qty}</td>
                  <td>{fmtUsd(t.entry_price)}</td>
                  <td>{fmtUsd(t.exit_price)}</td>
                  <td className="dim">{t.exit_time.slice(5, 10)}</td>
                  <td className="r">
                    <Pnl
                      v={t.pnl}
                      pct={t.entry_price > 0
                        ? `${(((t.entry_price - t.exit_price) / t.entry_price)
                            * (t.side === 'short' ? 100 : -100)).toFixed(1)}%`
                        : undefined}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="ledger rise" style={{ animationDelay: '270ms' }}>
        <div className="rule"><span className="rule-no">03</span><span className="rule-name">System</span></div>
        <div className="sys">
          {health ? Object.entries(health.subsystems).map(([k, v]) => (
            <span key={k} className={v.ok ? 'sys-chip ok' : 'sys-chip bad'}>{k}</span>
          )) : <span className="dim">…</span>}
        </div>
      </section>
    </main>
  );
}
